<template>
  <Teleport to="body">
    <Transition name="fcom_modal_fade">
      <div v-if="show" class="fcom_share_backdrop" @click.self="$emit('close')">
        <div class="fcom_share_card">
          <!-- Header -->
          <div class="fcom_share_header">
            <span class="fcom_share_title">{{ __('share_title') }}</span>
            <button class="fcom_share_close" :aria-label="__('close')" @click="$emit('close')">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <!-- Social links -->
          <div class="fcom_share_platforms">
            <a
              v-for="item in shareItems"
              :key="item.id"
              :href="item.href"
              :target="item.target || '_blank'"
              rel="noopener"
              class="fcom_share_item"
              @click="$emit('close')"
            >
              <span :class="['fcom_share_circle', 'fcom_share_circle--' + item.id]">
                <span class="fcom_share_icon" :style="{ color: item.color }" v-html="item.icon" />
              </span>
              <span class="fcom_share_label">{{ item.label }}</span>
            </a>
          </div>

          <!-- Copy link -->
          <div class="fcom_share_copy_section">
            <span class="fcom_share_copy_label">{{ __('or_share_with_link') }}</span>
            <div class="fcom_share_copy_row">
              <input
                ref="copyInput"
                class="fcom_share_copy_input"
                readonly
                :value="url"
                @click="copyInput?.select()"
              />
              <button
                class="fcom_share_copy_icon_btn"
                :title="copied ? __('copied') : __('copy_link')"
                @click="copyUrl"
              >
                <Transition name="fcom_icon-swap" mode="out-in">
                  <svg v-if="copied" key="check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <svg v-else key="copy" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                </Transition>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed } from 'vue'
import { __ } from '@/utils/i18n.js'

const props = defineProps({
  show:    { type: Boolean, default: false },
  title:   { type: String,  default: '' },
  summary: { type: String,  default: '' },
  url:     { type: String,  default: '' },
})

defineEmits(['close'])

const copied    = ref(false)
const copyInput = ref(null)

const encodedUrl   = computed(() => encodeURIComponent(props.url))
const encodedTitle = computed(() => encodeURIComponent(props.title))
const encodedText  = computed(() => encodeURIComponent(props.title + (props.summary ? ' — ' + props.summary : '')))

const shareItems = computed(() => [
  {
    id: 'twitter', label: __('x_twitter'), color: '#1d9bf0',
    href: `https://twitter.com/intent/tweet?text=${encodedText.value}&url=${encodedUrl.value}`,
    icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>',
  },
  {
    id: 'facebook', label: __('facebook'), color: '#1877f2',
    href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl.value}`,
    icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>',
  },
  {
    id: 'whatsapp', label: __('whatsapp'), color: '#25d366',
    href: `https://wa.me/?text=${encodedText.value}%20${encodedUrl.value}`,
    icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>',
  },
  {
    id: 'linkedin', label: __('linkedin'), color: '#0a66c2',
    href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl.value}`,
    icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>',
  },
  {
    id: 'email', label: __('email'), color: '#6b7280', target: '_self',
    href: `mailto:?subject=${encodedTitle.value}&body=${encodedText.value}%0A%0A${encodedUrl.value}`,
    icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 7L2 7"/></svg>',
  },
])

async function copyUrl() {
  try {
    await navigator.clipboard.writeText(props.url)
  } catch {
    copyInput.value?.select()
    document.execCommand('copy')
  }
  copied.value = true
  setTimeout(() => { copied.value = false }, 2000)
}
</script>
